import { Link } from "react-router-dom";
import { SITELINKS } from "../../../app-routes/links";
import { DashboardSummary } from "../../../interface/dashboard.interface";
import DashCard from "./DashCard";
import { fmtCount } from "./format";

const STATUSES = [
  { key: "pending", label: "Pending", color: "#F3A838" },
  { key: "accepted", label: "Accepted", color: "#22C55E" },
  { key: "rejected", label: "Rejected", color: "#EF4444" },
  { key: "closed", label: "Closed", color: "#94A3B8" },
];

const Bar = ({ parts }: { parts: { value: number; color: string; label: string }[] }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div className="flex h-2.5 overflow-hidden rounded-full bg-hcBg" aria-hidden>
      {total > 0 &&
        parts.map((p) => (
          <span key={p.label} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
        ))}
    </div>
  );
};

const RequestsCard = ({ data, className }: { data: DashboardSummary; className?: string }) => {
  const byStatus = data.requests?.by_status;
  const byGender = data.requests?.by_gender;
  const male = Number(byGender?.MALE) || 0;
  const female = Number(byGender?.FEMALE) || 0;
  const statusTotal = byStatus
    ? STATUSES.reduce((s, x) => s + (Number(byStatus[x.key]) || 0), 0)
    : undefined;
  const total = data.requests?.total ?? statusTotal ?? (male + female || undefined);

  return (
    <DashCard
      title="Connection requests"
      subtitle={total !== undefined ? `${total.toLocaleString()} requests` : undefined}
      empty={!byStatus && !byGender}
      className={className}
    >
      <p className="mb-2 text-xs font-medium text-hcMuted">By status</p>
      {byStatus ? (
        <>
          <Bar parts={STATUSES.map((s) => ({ ...s, value: Number(byStatus[s.key]) || 0 }))} />
          <ul className="mt-3 grid grid-cols-2 gap-1">
            {STATUSES.map((s) => (
              <li key={s.key}>
                <Link
                  to={`${SITELINKS.REQUEST}?status=${s.key}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-hcBg"
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-honeConnectGray">{s.label}</span>
                  <span className="ml-auto font-semibold text-honeConnectBlack">
                    {fmtCount(Number(byStatus[s.key]) || 0)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-xs text-gray-400">Waiting for requests.by_status from the API.</p>
      )}

      <div className="mt-5 border-t border-hcBorder pt-4">
        <p className="mb-2 text-xs font-medium text-hcMuted">Posted by</p>
        {male + female > 0 ? (
          <>
            <Bar
              parts={[
                { label: "Male", value: male, color: "#7600BC" },
                { label: "Female", value: female, color: "#F3A838" },
              ]}
            />
            <div className="mt-2 flex justify-between text-xs text-hcMuted">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-honeConnectPurple" />
                Male {fmtCount(male)} ({((male / (male + female)) * 100).toFixed(0)}%)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-honeConnectOrange" />
                Female {fmtCount(female)} ({((female / (male + female)) * 100).toFixed(0)}%)
              </span>
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400">No requests yet.</p>
        )}
      </div>
    </DashCard>
  );
};

export default RequestsCard;
